import EmptyState from 'components/EmptyState/EmptyState';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import 'features/Weather/assets/weatherDataDisplay.css';
import WeatherCardGrid from 'features/Weather/components/WeatherCardGrid/WeatherCardGrid';
import { getForecastDateLabel } from 'features/Weather/utils/forecastDate.utils';
import useLocation from 'hooks/useLocation';
import { useMemo } from 'react';
import { Container } from 'react-bootstrap';
import { PiWaves } from 'react-icons/pi';
import { useGetTideTableQuery } from 'services/weather/marine/marine.api';
import MarineAccordion from '../../components/MarineAccordion/MarineAccordion';
import './assets/tideTable.scss';
import { route, tideForecastDetails } from './config/tideTable.config';
import {
  getTideForecastCardKey,
  getTideForecastCardTitle,
  getTideLocationSubtitle,
  getTideTableLocations,
} from './utils/tideTable.utils';

const TideTable = () => {
  const { locations } = useLocation();
  const effectiveLocation = locations.effectiveLocation;
  const {
    data: tideEntries = [],
    isLoading: isLoadingTideTable,
    isFetching: isFetchingTideTable,
    fulfilledTimeStamp,
  } = useGetTideTableQuery(effectiveLocation);
  const isRefreshing = isFetchingTideTable && !isLoadingTideTable;

  const tideLocations = useMemo(() => getTideTableLocations(tideEntries), [tideEntries]);

  return (
    <Container
      fluid
      className="tide-table weather-data-display d-flex h-100 flex-column gap-3 px-0 text-white user-select-none"
    >
      <WidgetTimeUpdate route={route} updatedAt={fulfilledTimeStamp ?? null} />

      {isLoadingTideTable ? (
        <div className="d-flex h-100 align-items-center justify-content-center mt-3">
          <WidgetLoadingAnimation />
        </div>
      ) : !tideLocations.length ? (
        <EmptyState
          title="Tide Table"
          message="No tide forecast data available for this location."
        />
      ) : (
        <>
          <div className="weather-data-display-heading d-flex align-items-start justify-content-between gap-3 border-bottom pb-2">
            <h2 className="weather-data-display-title m-0 fw-bold lh-sm">
              {getForecastDateLabel(new Date(), 'Tide Table')}
            </h2>

            {isRefreshing && (
              <span className="tide-table-page-status small" role="status">
                Updating...
              </span>
            )}
          </div>

          <div className="tide-table-scroll flex-grow-1 overflow-auto">
            <div className="d-flex flex-column gap-3 pb-4">
              {tideLocations.map((location) => (
                <MarineAccordion
                  key={location.locationKey}
                  eventKey={location.locationKey}
                  title={location.locationName}
                  subtitle={getTideLocationSubtitle(location)}
                  headingLevel="h3"
                >
                  <WeatherCardGrid
                    entries={location.forecasts}
                    details={tideForecastDetails}
                    getCardKey={getTideForecastCardKey}
                    getCardTitle={getTideForecastCardTitle}
                    getCardIcon={() => <PiWaves size={56} />}
                    containerClassName="tide-table-card-grid"
                    detailsContainerClassName="tide-table-card-details gap-2"
                    cardHeadingLevel="h4"
                    emptyMessage={`No tide forecasts available for ${location.locationName}.`}
                  />
                </MarineAccordion>
              ))}
            </div>
          </div>
        </>
      )}
    </Container>
  );
};

export default TideTable;
